import type { FoodTemplate, MealLog, MealType } from '@/api/types/nutrition'

/** その日の食事記録から次に登録すべき食事タイプを推定する。 */
export function getNextMealType(meals: MealLog[]): MealType {
  const types = new Set(meals.map((m) => m.meal_type))
  if (!types.has('breakfast')) return 'breakfast'
  if (!types.has('lunch')) return 'lunch'
  if (!types.has('dinner')) return 'dinner'
  return 'snack'
}

/** 現在時刻を "HH:mm" 形式で返す。 */
export function currentTime(): string {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

/** "HH:mm" または "HH:mm:ss" を BE が期待する "HH:mm:ss" 形式へ正規化。空文字は null。 */
export function inputToTime(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  return trimmed.length === 5 ? `${trimmed}:00` : trimmed
}

/** BEから返る "HH:MM:SS" を input[type=time] 用の "HH:MM" へ変換。 */
export function timeToInput(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 5)
}

/**
 * quantity 文字列に scale を反映した表示文字列を返す。
 *
 * - quantity に数値が含まれる場合は最初の数値部分を scale 倍する。
 *   例: scaleQuantity("100g", 2) → "200g"、scaleQuantity("1個", 0.5) → "0.5個"
 * - 数値が含まれない（"少々" 等）、または scale === 1 の場合は元の文字列を返す。
 *   scale !== 1 で数値なしの場合は ` ×N` を併記する。例: "少々 ×2"
 * - quantity が null/空文字の場合は空文字を返す（呼び出し側で表示制御）。
 */
export function scaleQuantity(quantity: string | null | undefined, scale: number): string {
  if (!quantity) return ''
  if (!Number.isFinite(scale) || scale === 1) return quantity

  const range = findFirstNumber(quantity)
  if (range) {
    const numText = quantity.slice(range.start, range.end)
    const scaled = parseFloat(numText) * scale
    const formatted = formatScaledNumber(scaled)
    return quantity.slice(0, range.start) + formatted + quantity.slice(range.end)
  }

  const scaleLabel = formatScaledNumber(scale)
  return `${quantity} ×${scaleLabel}`
}

/** 文字列内で最初に登場する数値（小数も対応）の位置を返す。 */
function findFirstNumber(s: string): { start: number; end: number } | null {
  let i = 0
  // 先頭の非数値文字をスキップ。'-' の直後が数字ならマイナス込みで始める。
  while (i < s.length && !isDigitAt(s, i)) {
    if (s[i] === '-' && i + 1 < s.length && isDigitAt(s, i + 1)) break
    i++
  }
  if (i >= s.length) return null

  const start = i
  if (s[i] === '-') i++
  while (i < s.length && isDigitAt(s, i)) i++
  if (i < s.length && s[i] === '.' && i + 1 < s.length && isDigitAt(s, i + 1)) {
    i++
    while (i < s.length && isDigitAt(s, i)) i++
  }
  return { start, end: i }
}

function isDigitAt(s: string, i: number): boolean {
  const ch = s.charCodeAt(i)
  return ch >= 0x30 && ch <= 0x39
}

function formatScaledNumber(n: number): string {
  if (Number.isInteger(n)) return String(n)
  return parseFloat(n.toFixed(2)).toString()
}

/**
 * 食品 1 件だけのお気に入りの名前（記録済み食品の ⭐ 登録済み判定に使う）
 * お気に入り名を変えても判定が外れないよう、中の食品名も含める
 */
export function singleItemTemplateNames(templates: FoodTemplate[]): Set<string> {
  const names = new Set<string>()
  for (const t of templates) {
    const [only, ...rest] = t.items
    if (!only || rest.length > 0) continue
    names.add(t.name.trim())
    names.add(only.name.trim())
  }
  return names
}

/** "YYYY-MM-DD" を "M/D" にする（例: "2026-10-27" → "10/27"） */
export function formatMonthDay(isoDate: string): string {
  const [, month, day] = isoDate.split('-')
  return `${Number(month)}/${Number(day)}`
}
